import { ChangeEvent, useEffect, useState } from 'react';
import Slider from '@mui/material/Slider';
import styled from 'styled-components';
import { ui } from './tokens';

export type ValueSliderProps = {
	label: string;
	value: number;
	min: number;
	max: number;
	step?: number;
	onChange: (value: number) => void;
	/** Lets the value bubble be typed into for exact numbers */
	editable?: boolean;
	/** Gold track variant (style guide R14 step 3) instead of the white hairline */
	variant?: 'white' | 'gold';
	/** Optional formatter for the label suffix, e.g. "Opacity: 46%" */
	inlineValue?: (value: number) => string;
	ariaLabel?: string;
};

/**
 * Labelled slider from the style guide: label on the left, thin track, grey
 * thumb and a round grey value bubble on the right.
 */
function ValueSlider({ label, value, min, max, step = 1, onChange, editable, variant = 'white', inlineValue, ariaLabel }: ValueSliderProps) {
	const [draft, setDraft] = useState(String(value));

	useEffect(() => {
		setDraft(String(value));
	}, [value]);

	const clamp = (n: number) => Math.min(max, Math.max(min, n));

	const commitDraft = () => {
		const n = parseInt(draft, 10);
		if (isNaN(n)) {
			setDraft(String(value));
			return;
		}
		const clamped = clamp(n);
		setDraft(String(clamped));
		onChange(clamped);
	};

	if (inlineValue) {
		return (
			<Stacked>
				<Label>{inlineValue(value)}</Label>
				<StyledSlider $variant={variant} value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v as number)} aria-label={ariaLabel ?? label} />
			</Stacked>
		);
	}

	return (
		<Row>
			<Label>{label}</Label>
			<StyledSlider $variant={variant} value={value} min={min} max={max} step={step} onChange={(_, v) => onChange(v as number)} aria-label={ariaLabel ?? label} />
			{editable ? (
				<BubbleInput
					value={draft}
					inputMode="numeric"
					aria-label={`${label} value`}
					onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
					onBlur={commitDraft}
					onKeyDown={e => {
						if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
					}}
				/>
			) : (
				<Bubble>{value}</Bubble>
			)}
		</Row>
	);
}

export default ValueSlider;

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	margin-top: 12px;
`;

const Stacked = styled.div`
	margin-top: 14px;
`;

const Label = styled.span`
	min-width: 56px;
	font-size: 15px;
	color: ${ui.text};
	font-family: ${ui.font};
`;

const StyledSlider = styled(Slider).withConfig({ shouldForwardProp: prop => prop !== '$variant' })<{ $variant: 'white' | 'gold' }>`
	flex: 1;
	padding: 14px 0 !important;

	.MuiSlider-rail {
		background-color: ${props => (props.$variant === 'gold' ? ui.gold : ui.text)};
		opacity: 1;
		height: 2px;
	}
	.MuiSlider-track {
		background-color: ${props => (props.$variant === 'gold' ? ui.gold : ui.text)};
		border: none;
		height: 2px;
	}
	.MuiSlider-thumb {
		width: 18px;
		height: 18px;
		background-color: ${props => (props.$variant === 'gold' ? ui.gold : ui.label)};
		box-shadow: none !important;
	}
`;

const bubble = `
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background-color: ${ui.label};
	border: 2px solid ${ui.input};
	color: #1A1A1A;
	font-family: ${ui.font};
	font-size: 11px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

const Bubble = styled.div`
	${bubble}
`;

const BubbleInput = styled.input`
	${bubble}
	text-align: center;
	padding: 0;
	outline: none;

	&:focus {
		border-color: ${ui.gold};
	}
`;
